import { useCallback, useEffect, useMemo, useState } from 'react';
import { EMOTIONS, type Emotion } from '@shared/constants';
import { Button } from '@/components/Button';
import { FlexColPageContainer } from '@/components/FlexColPageContainer';
import { ScrollShadow } from '@/components/ScrollShadow';
import { navigate } from '@/features/routing';
import { getDebugRing } from '@/lib/devUtils';
import { useMoodRecordDraft } from '@/lib/moodUtils/MoodRecordDraftContext';
import { getMoodUI } from '@/lib/moodUtils/getMoodUIDetails';
import { clx } from '@/lib/utils';
import { ContinueButton } from '../ContinueButton';
import { Heading } from '../Heading';

interface IEmotionsPickerFormProps {
  userId: string;
  displayName: string;
}
export const EmotionsPickerForm = ({ displayName }: IEmotionsPickerFormProps) => {
  const { moodRecordDraft, updateMoodRecordDraft } = useMoodRecordDraft();

  const moodUI = useMemo(() => {
    return moodRecordDraft?.mood ? getMoodUI(moodRecordDraft.mood) : null;
  }, [moodRecordDraft?.mood]);

  const [emotions, _setEmotions] = useState<Emotion[]>(moodRecordDraft?.emotions || []);
  const [shouldShowCTA, setShouldShowCTA] = useState(emotions.length === 2);

  useEffect(() => {
    if (!shouldShowCTA) {
      setShouldShowCTA(emotions.length === 2);
    }
  }, [shouldShowCTA, emotions]);

  const toggleEmotion = useCallback(
    (emotion: Emotion) => {
      if (emotions.includes(emotion)) {
        _setEmotions((prev) => prev.filter((e) => e !== emotion));
      } else {
        _setEmotions((prev) => [...prev, emotion]);
      }
    },
    [emotions, _setEmotions]
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (emotions.length === 2) {
      const success = await updateMoodRecordDraft({ emotions });
      if (success) {
        navigate(`/mood-log-entry/step3`);
      } else {
        // TODO: Handle this error. Could add toast notification here.
      }
    } else {
      // TODO: Handle this other error case. Could add toast notification here.
    }
  };

  if (!moodRecordDraft) {
    // @TODO: show loading overlay here.
    return (
      <FlexColPageContainer>
        <div className="mx-auto my-3 w-10/12 text-center">
          <h1 className="text-2xl text-black">Loading...</h1>
        </div>
      </FlexColPageContainer>
    );
  }

  if (!moodRecordDraft?.mood) {
    return (
      <FlexColPageContainer>
        <div className="mx-auto my-3 w-10/12 text-center">
          <h1 className="text-2xl text-black">Please select your mood first.</h1>
          <Button onClick={() => history.go(-1)} variant="buttonPrimary">
            Go to Step 1
          </Button>
        </div>
      </FlexColPageContainer>
    );
  }

  const { bgColor } = moodUI || {};

  return (
    <FlexColPageContainer className="justify-start pt-2.5">
      <Heading className="mb-9">
        Tell more about your day,
        <br />
        {displayName}.
        <br />
        You are feeling: <strong>{moodRecordDraft.mood}</strong> and ...
      </Heading>

      <form
        onSubmit={handleSubmit}
        className={clx('flex min-h-48 shrink basis-full flex-col', getDebugRing('blue'))}
      >
        <ScrollShadow
          innerClassName={clx(
            'mx-auto grid min-h-36 shrink grid-cols-2 gap-5',
            getDebugRing('red')
          )}
        >
          {Object.values(EMOTIONS).map((emotion) => {
            const isSelected = emotions.includes(emotion);
            return (
              <Button
                className={clx(
                  'w-30 flex h-10 items-center justify-center p-0 drop-shadow',
                  isSelected ? bgColor : ''
                )}
                key={emotion}
                name="emotion"
                onClick={() => toggleEmotion(emotion)}
                type="button"
                value={emotion}
                variant="buttonPrimary"
              >
                {emotion}
              </Button>
            );
          })}
        </ScrollShadow>

        <ContinueButton
          className={clx(
            'transition-all duration-100',
            shouldShowCTA ? '' : 'h-0 overflow-hidden',
            getDebugRing('orange')
          )}
          disabled={!shouldShowCTA || emotions.length !== 2}
        />
      </form>
    </FlexColPageContainer>
  );
};
